import type { PhotoSize } from './photo-crop';
import { UNREADABLE_MESSAGE } from './photo-files';
import { shrunkSize } from './photo-shrink-plan';

const JPEG_QUALITY = 0.92;

export interface ShrunkPhoto {
  file: File;
  size: PhotoSize;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(UNREADABLE_MESSAGE));
    image.src = url;
  });
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(UNREADABLE_MESSAGE))),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

const jpegName = (name: string) => `${name.replace(/\.[^.]*$/, '') || 'photo'}.jpg`;

/**
 * Redraws a big photo smaller, as a JPEG on a white background (so see-through areas of a PNG or
 * WebP do not turn black).
 *
 * It draws from an <img> rather than a bitmap because browsers show an <img> upright using the
 * photo's rotation data by default, so the result is upright with no rotation data left, and the
 * crop coordinates used afterwards are relative to this smaller picture.
 */
export async function shrinkPhoto(
  file: File,
  url: string,
  original: PhotoSize,
): Promise<ShrunkPhoto> {
  const size = shrunkSize(original);
  const image = await loadImage(url);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  try {
    const context = canvas.getContext('2d');
    if (!context) throw new Error(UNREADABLE_MESSAGE);
    context.fillStyle = '#fff';
    context.fillRect(0, 0, size.width, size.height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, size.width, size.height);
    const blob = await toJpeg(canvas);
    return { file: new File([blob], jpegName(file.name), { type: 'image/jpeg' }), size };
  } finally {
    // Frees the big pictures' memory straight away rather than waiting for the browser.
    canvas.width = 0;
    canvas.height = 0;
    image.src = '';
  }
}
